/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Frequently Asked Questions section with expandable items
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-lg font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is an AI video translator?',
      answer:
        'An AI video translator is a tool that converts the audio, subtitles, or voiceover of a video into another language. FalcoCut AI Video Translator uses AI voices, voice cloning, and lip sync to create natural, localized content that helps you connect with global audiences.',
    },
    {
      id: 'faq-2',
      question: 'How many languages does FalcoCut AI Video Translator support?',
      answer:
        'FalcoCut AI Video Translator supports translation in over 35 languages and dialects, including English, Spanish, French, Japanese, Arabic, Hindi, and more.',
    },
    {
      id: 'faq-3',
      question: 'Does FalcoCut AI Video Translator support subtitles and dubbing?',
      answer:
        'Yes. FalcoCut can generate accurate subtitles with customization options (fonts, size, color) and also provide AI dubbing with voice cloning and lip sync, so your video feels authentic in the target language.',
    },
    {
      id: 'faq-4',
      question: 'Can I preserve my own voice when translating?',
      answer:
        'Yes! FalcoCut uses AI voice cloning to preserve your unique voice in the target language. This means your translated videos will still sound like you, maintaining authenticity and personal connection with your audience.',
    },
    {
      id: 'faq-5',
      question: "How accurate is FalcoCut's video translation?",
      answer:
        'FalcoCut uses advanced AI and machine learning to deliver highly accurate translations. Our AI ensures context-aware translations that maintain the meaning and cultural nuances of the original content.',
    },
    {
      id: 'faq-6',
      question: 'How fast is the translation process?',
      answer:
        "FalcoCut's AI works much faster than traditional translation methods. Most videos are processed in minutes, depending on the length and complexity of the content.",
    },
    {
      id: 'faq-7',
      question: "What's the difference between subtitles and full AI video translation?",
      answer:
        'Subtitles only provide text overlays in another language, while full AI video translation with FalcoCut includes translated voiceovers, lip sync, and natural AI dubbing. This creates a fully localized video experience.',
    },
    {
      id: 'faq-8',
      question: 'Who can benefit from FalcoCut AI Video Translator?',
      answer:
        "Content creators, marketers, educators, businesses, and anyone looking to reach global audiences can benefit from FalcoCut. It's perfect for YouTube videos, marketing campaigns, training materials, and more.",
    },
    {
      id: 'faq-9',
      question: 'Can I translate an audio file or podcast?',
      answer:
        'Yes! FalcoCut can translate audio files and podcasts into different languages with AI voice cloning and natural dubbing.',
    },
    {
      id: 'faq-10',
      question: 'How can I download my translated videos and subtitles?',
      answer:
        'After your video is processed, go to the My Creations section on the right side of the interface. Click the menu icon (•••) next to your video and choose Download. Your translated video will be downloaded as an MP4 file with subtitles or dubbing already embedded.',
    },
    {
      id: 'faq-11',
      question: 'What is the best video translator?',
      answer: (
        <>
          <p className="mb-3">
            FalcoCut AI Video Translator is widely regarded as one of the best for its
            accuracy, speed, and advanced features. Here&apos;s how it compares to
            alternatives:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              FalcoCut – Supports 35 languages with subtitles, dubbing, and AI voice
              cloning. Fast, accurate, and flexible for e-learning, e-commerce, and
              marketing.
            </li>
            <li>
              HeyGen: Great for avatar-based videos but limited for traditional video
              translation.
            </li>
            <li>Captions: Focuses on auto-captioning with basic translation features.</li>
            <li>
              Rask AI: Offers dubbing but lacks the same depth in voice cloning and lip
              sync.
            </li>
            <li>
              Kapwing: Simple tool for subtitles, but not optimized for full AI video
              dubbing.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: 'faq-12',
      question: 'How can I translate a video to English?',
      answer:
        'Simply upload your video to FalcoCut, select English as the target language, and let the AI handle the rest. You can customize subtitle styles, choose voice settings, and download your translated video when ready.',
    },
    {
      id: 'faq-13',
      question: 'How can I translate audio from a video?',
      answer:
        'FalcoCut automatically extracts and translates the audio from your video. You can generate subtitles or create a full AI-dubbed version in the target language with voice cloning and lip sync.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
